import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Input, InputGroup, InputRightAddon, Stack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { analyticsIds } from '../../../../../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../../../../../constants/cypressIds';
import { hideModal } from '../../../../../../../../../../../../../../redux/modules/modal';
import { useUpdateDocumentName } from '../../../../../../../../../../../../../../services/document-service';
import { useActiveRecordDocuments } from '../../../../../../../../../../../../../../services/record-service';

const EditDocumentNameModal = ({ docId, insuredId, jobId, recordId }) => {
  const dispatch = useDispatch();
  const { data: documents } = useActiveRecordDocuments({ recordId });
  const { mutate: updateDocumentName } = useUpdateDocumentName({ docId, recordId });

  const document = useMemo(() => documents.find(d => d.id === docId), [documents, docId]);

  const { displayName, originalName } = document;

  let docExtension = '';
  let docName = displayName || originalName;
  const indexOfLastDot = docName.lastIndexOf('.');

  if (indexOfLastDot > -1) {
    docExtension = docName.substr(indexOfLastDot);
    docName = docName.substr(0, indexOfLastDot);
  }

  const getInputAddon = () => (docExtension ? <InputRightAddon children={docExtension} /> : '');
  const [value, setValue] = useState(docName);
  const [isValid, setIsValid] = useState([true, true]);
  const allowedCharacters = 'a-z A-Z 0-9 _ - . ( ) space';

  const checkValidName = e => {
    const customName = e.target.value;
    const validRegex = /^[a-zA-Z0-9 _().-]+$/;

    if (customName === '' || validRegex.test(customName)) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }

    setValue(e.target.value);
  };

  return (
    <Box mt={4}>
      <Stack spacing={5}>
        <Text>Enter Custom Document Name: </Text>
        <InputGroup>
          <Input
            data-cy={CypressIds.editDocumentNameInput}
            defaultValue={docName}
            errorBorderColor="error.primary.600"
            isInvalid={!isValid}
            name="customName"
            type="text"
            onChange={e => {
              checkValidName(e);
            }}
          />
          {getInputAddon()}
        </InputGroup>
        {(() => {
          if (!isValid) {
            return (
              <Text>
                {`A filename can only contain the following characters: ${allowedCharacters}`}
              </Text>
            );
          }
        })()}
        <Text textStyle="bodyMd">Leave this field empty to use the default file name.</Text>
        <Button
          isRound
          data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.updateDocumentNameButton}
          data-cy={CypressIds.updateDocumentNameButton}
          isDisabled={!isValid}
          mt={4}
          size="lg"
          variant="gradientPrimary"
          onClick={async () => {
            const nameToChange = value && value.trim() !== '' ? `${value}${docExtension}` : document.originalName;

            updateDocumentName({ name: nameToChange });

            dispatch(hideModal());
          }}
        >
          Update Document Name
        </Button>
      </Stack>
    </Box>
  );
};

EditDocumentNameModal.propTypes = {
  docId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired
};

export default EditDocumentNameModal;
